import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { PropsWithChildren, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import styles from "./DragAndDrop.module.scss";

interface DragItem {
    index: number;
    id: string;
    type: string;
}

/**
 * Props for DragAndDropCard
 * @prop id and index - card id and index
 * @prop applyAction - to apply some action at the end of dragging (e.g. fetch)
 * @prop onMoveCard - method, called during card moving to set new card order
 * @prop type - unique string, must be equal for useDrag "accept" field and useDrop "type" prop
 * @prop canDrag - if "false" prevents item from dragging
 */

interface DragAndDropCardProps {
    id: string;
    index: number;
    applyAction: () => void;
    onMoveCard: (dragIndex: number, hoverIndex: number) => void;
    type: string;
    canDrag?: boolean;
}

export const DragAndDropCard = ({
    id,
    index,
    applyAction,
    onMoveCard,
    children,
    type,
    canDrag,
}: PropsWithChildren<DragAndDropCardProps>) => {
    const ref = useRef<HTMLDivElement>(null);
    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null }>({
        accept: type,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY =
                (
                    clientOffset as {
                        x: number;
                        y: number;
                    }
                ).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            onMoveCard(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type,
        item: () => {
            return { id, index };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            applyAction();
        },
        canDrag: (monitor) => {
            return canDrag ?? true;
        },
    });

    const opacity = isDragging ? 0 : 1;

    drag(drop(ref));
    return (
        <div
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            className={`${styles.cardWrapper} ${
                canDrag === false ? styles.disabledCardWrapper : ""
            }`}
        >
            <Icon icon={IconCollection.drag} />
            {children}
        </div>
    );
};
